import request from '@/config/axios'
import type { PageParam } from '@/types/common'
import type { PageResult } from '@/api/lawyer'

export interface FirmTimeEntryVO {
  id?: number
  caseId: number
  caseTitle?: string
  firmCaseNo?: string
  lawyerId?: number
  lawyerName?: string
  workDate: string
  minutes: number
  workType: string
  content: string
  billable?: boolean
  status?: string
  hourlyRateSnapshot?: number
  amountSnapshot?: number
  voidOfId?: number
  redInk?: boolean
  approveLawyerId?: number
  approveLawyerName?: string
  approveTime?: string
  rejectReason?: string
  createTime?: string
}

export interface FirmTimeEntryPageReqVO extends PageParam {
  caseId?: number
  lawyerId?: number
  status?: string
  workDateBegin?: string
  workDateEnd?: string
  pendingOnly?: boolean
}

export interface FirmTimeEstimateVO {
  billableMinutes: number
  amount: number
  billingMode?: string
}

export interface FirmFeeRateVO {
  id?: number
  scope: string
  lawyerLevel?: string
  caseId?: number
  caseTitle?: string
  hourlyRate: number
  currency?: string
  effectiveFrom?: string
  effectiveTo?: string
  enabled?: boolean
  remark?: string
  createTime?: string
}

export interface FirmFeeRatePageReqVO extends PageParam {
  scope?: string
  lawyerLevel?: string
  caseId?: number
}

const TIME_STATUS_OPTIONS = [
  { value: 'DRAFT', label: '草稿' },
  { value: 'PENDING', label: '待审' },
  { value: 'APPROVED', label: '已确认' },
  { value: 'BILLED', label: '已出账' },
  { value: 'VOID', label: '作废' }
] as const

const FEE_RATE_SCOPE_OPTIONS = [
  { value: 'LEVEL', label: '级别默认' },
  { value: 'MATTER', label: '个案覆盖' }
] as const

const FIRM_LEVEL_OPTIONS = [
  { value: 'JUNIOR', label: '初级' },
  { value: 'MIDDLE', label: '中级' },
  { value: 'SENIOR', label: '高级' },
  { value: 'PARTNER', label: '合伙人' }
] as const

export function timeStatusLabel(code?: string) {
  return TIME_STATUS_OPTIONS.find((o) => o.value === code)?.label || code || '-'
}

export const FirmTimeApi = {
  create: (data: Partial<FirmTimeEntryVO>) =>
    request.post({ url: '/lawyer/firm/time-entry/create', data }),
  update: (data: Partial<FirmTimeEntryVO>) =>
    request.put({ url: '/lawyer/firm/time-entry/update', data }),
  delete: (id: number) => request.delete({ url: '/lawyer/firm/time-entry/delete', params: { id } }),
  get: (id: number) =>
    request.get({ url: '/lawyer/firm/time-entry/get', params: { id } }) as Promise<FirmTimeEntryVO>,
  page: (params: FirmTimeEntryPageReqVO) =>
    request.get({ url: '/lawyer/firm/time-entry/page', params }) as Promise<PageResult<FirmTimeEntryVO>>,
  submit: (id: number) => request.put({ url: '/lawyer/firm/time-entry/submit', data: { id } }),
  approve: (data: { id: number; passed: boolean; rejectReason?: string }) =>
    request.put({ url: '/lawyer/firm/time-entry/approve', data }),
  void: (data: { id: number; reason: string }) =>
    request.put({ url: '/lawyer/firm/time-entry/void', data }),
  redInk: (data: { voidOfId: number; minutes: number; reason: string }) =>
    request.post({ url: '/lawyer/firm/time-entry/red-ink', data }),
  estimate: (caseId: number) =>
    request.get({ url: '/lawyer/firm/time-entry/estimate', params: { caseId } }) as Promise<FirmTimeEstimateVO>
}

export const FirmFeeRateApi = {
  create: (data: FirmFeeRateVO) => request.post({ url: '/lawyer/firm/fee-rate/create', data }),
  update: (data: FirmFeeRateVO) => request.put({ url: '/lawyer/firm/fee-rate/update', data }),
  get: (id: number) =>
    request.get({ url: '/lawyer/firm/fee-rate/get', params: { id } }) as Promise<FirmFeeRateVO>,
  page: (params: FirmFeeRatePageReqVO) =>
    request.get({ url: '/lawyer/firm/fee-rate/page', params }) as Promise<PageResult<FirmFeeRateVO>>
}

export { TIME_STATUS_OPTIONS, FEE_RATE_SCOPE_OPTIONS, FIRM_LEVEL_OPTIONS }
